import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ClipboardList, Plus, Trash2, ChevronDown, CheckCircle2, Circle, RefreshCw, Briefcase, HeartHandshake,
  Lock, Eye, EyeOff, Download, Sparkles, Wand2, Loader2,
} from 'lucide-react';

import UpgradeNudge from '../../components/portal/UpgradeNudge';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_URL, withCredentials: true });

// base: '/api/admin/clarity-hub' | '/api/portal/clarity-hub' ; q: '?person=self' | ''
export const VenturesPanel = ({ base, q = '', allowDelete = true }) => {
  const [fields, setFields] = useState([]);
  const [ventures, setVentures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [name, setName] = useState('');
  const [type, setType] = useState('business');
  const [autofilling, setAutofilling] = useState(null);
  const [autofillUrls, setAutofillUrls] = useState({});
  const timers = useRef({});
  const [limit, setLimit] = useState(null);
  const [nudge, setNudge] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`${base}/ventures${q}`);
      setFields(r.data.fields || []);
      setLimit(r.data.business_limit || null);
      setVentures(r.data.ventures || []);
    } catch (e) { toast.error('Could not load essentials: ' + (e.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  }, [base, q]);
  useEffect(() => { load(); }, [load]);

  const addVenture = async () => {
    if (!name.trim()) { toast.error('Name your business or nonprofit'); return; }
    try {
      const r = await api.post(`${base}/ventures${q}`, { name: name.trim(), type });
      setVentures((v) => [...v, r.data]);
      setOpenId(r.data.venture_id); setName(''); setType('business'); setAdding(false);
    } catch (e) {
      if (e.response?.data?.detail?.code === 'business_limit') { setAdding(false); setNudge(true); return; }
      toast.error('Add failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const patch = (id, patchBody, optimistic) => {
    if (optimistic) setVentures((vs) => vs.map((v) => (v.venture_id === id ? { ...v, ...optimistic } : v)));
    const sep = q ? '&' : '?';
    const key = id + JSON.stringify(Object.keys(patchBody.essentials || patchBody));
    if (timers.current[key]) clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(async () => {
      try {
        const r = await api.patch(`${base}/ventures/${id}${q}`, patchBody);
        setVentures((vs) => vs.map((v) => (v.venture_id === id ? r.data : v)));
      } catch (e) { toast.error('Save failed: ' + (e.response?.data?.detail || e.message)); }
    }, 700);
  };

  const setEssential = (v, fid, val) => {
    const essentials = { ...(v.essentials || {}), [fid]: val };
    patch(v.venture_id, { essentials: { [fid]: val } }, { essentials });
  };

  const removeVenture = async (id) => {
    if (!window.confirm('Delete this venture and its essentials?')) return;
    try { await api.delete(`${base}/ventures/${id}${q}`); setVentures((vs) => vs.filter((v) => v.venture_id !== id)); }
    catch (e) { toast.error('Delete failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const autofill = async (v) => {
    setAutofilling(v.venture_id);
    try {
      const r = await api.post(`${base}/ventures/${v.venture_id}/autofill${q}`, { website_url: (autofillUrls[v.venture_id] || '').trim() || undefined });
      setVentures((vs) => vs.map((x) => (x.venture_id === v.venture_id ? r.data : x)));
      toast.success(r.data.applied ? `AI filled ${r.data.applied} field${r.data.applied > 1 ? 's' : ''}` : 'No new details found to fill');
    } catch (e) { toast.error('Auto-fill failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setAutofilling(null); }
  };

  const groups = [...new Set(fields.map((f) => f.group))];
  const atLimit = !!limit && ventures.length >= limit;

  return (
    <div data-testid="ventures-panel">
      <UpgradeNudge open={nudge} onOpenChange={setNudge} limit={limit || 1} />
      {atLimit && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-orange-200 bg-orange-50 px-4 py-2.5 text-sm text-orange-800" data-testid="venture-limit-banner">
          <span>You're using {ventures.length} of {limit} business on your plan.</span>
          <button type="button" onClick={() => setNudge(true)} data-testid="venture-limit-upgrade" className="font-bold text-[#C2410C] hover:underline">Upgrade for unlimited</button>
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-black uppercase tracking-wider text-[#0A0F1C] flex items-center gap-2"><ClipboardList size={16} className="text-[#1D4ED8]" /> Business Essentials</h3>
          <p className="text-xs text-[#64748B]">One central place for each venture's key facts — name, address, phone, website, EIN & more.</p>
        </div>
        {!adding && <button type="button" onClick={() => (atLimit ? setNudge(true) : setAdding(true))} data-testid="venture-add-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold bg-[#1D4ED8] text-white rounded-lg hover:bg-[#1E40AF]"><Plus size={14} /> Add venture</button>}
      </div>

      {adding && (
        <div className="mb-4 p-4 border border-[#E2E8F0] rounded-xl bg-slate-50 flex flex-wrap items-end gap-3" data-testid="venture-add-form">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-[11px] font-bold text-[#64748B] mb-1">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="venture-name-input" placeholder="e.g. Greater Community Impact" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]" />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-[#64748B] mb-1">Type</label>
            <div className="flex gap-1">
              {['business', 'nonprofit'].map((t) => (
                <button type="button" key={t} onClick={() => setType(t)} data-testid={`venture-type-${t}`} className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg border ${type === t ? 'border-[#1D4ED8] bg-[#EFF6FF] text-[#1D4ED8]' : 'border-[#E2E8F0] text-[#475569]'}`}>
                  {t === 'business' ? <Briefcase size={13} /> : <HeartHandshake size={13} />} {t === 'business' ? 'Business' : 'Nonprofit'}
                </button>
              ))}
            </div>
          </div>
          <button type="button" onClick={addVenture} data-testid="venture-save-btn" className="px-4 py-2 text-sm font-bold bg-[#1D4ED8] text-white rounded-lg hover:bg-[#1E40AF]">Add</button>
          <button type="button" onClick={() => setAdding(false)} className="px-3 py-2 text-sm font-bold text-[#64748B]">Cancel</button>
        </div>
      )}

      {loading ? (
        <div className="text-center text-sm text-[#64748B] py-6"><RefreshCw className="animate-spin inline mr-2" size={16} /> Loading…</div>
      ) : ventures.length === 0 ? (
        <div className="text-sm text-[#64748B] border border-dashed border-[#E2E8F0] rounded-xl p-6 text-center">No ventures yet. Add a business or nonprofit to start its essentials checklist.</div>
      ) : (
        <div className="space-y-3">
          {ventures.map((v) => {
            const pct = Math.round((v.filled / v.total) * 100);
            const open = openId === v.venture_id;
            return (
              <div key={v.venture_id} data-testid={`venture-${v.venture_id}`} className="border border-[#E2E8F0] rounded-xl overflow-hidden">
                <button type="button" onClick={() => setOpenId(open ? null : v.venture_id)} data-testid={`venture-toggle-${v.venture_id}`} className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 text-left">
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center text-white shrink-0 ${v.type === 'nonprofit' ? 'bg-fuchsia-600' : 'bg-[#1D4ED8]'}`}>{v.type === 'nonprofit' ? <HeartHandshake size={16} /> : <Briefcase size={16} />}</span>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-[#0A0F1C] truncate">{v.name}</div>
                    <div className="text-[11px] text-[#64748B]">{v.filled}/{v.total} essentials captured</div>
                  </div>
                  <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden hidden sm:block"><div className="h-full bg-gradient-to-r from-[#1D4ED8] to-fuchsia-500" style={{ width: `${pct}%` }} /></div>
                  <span className="text-xs font-black text-[#0A0F1C] w-9 text-right">{pct}%</span>
                  <ChevronDown size={16} className={`text-[#94A3B8] transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
                {open && (
                  <div className="p-4 border-t border-[#F1F5F9] bg-slate-50/50 space-y-4" data-testid={`venture-body-${v.venture_id}`}>
                    <div className="rounded-lg border border-[#BFDBFE] bg-[#EFF6FF] p-3" data-testid={`venture-autofill-${v.venture_id}`}>
                      <div className="flex items-center gap-1.5 text-xs font-black text-[#1D4ED8] mb-1"><Sparkles size={14} /> AI Auto-fill</div>
                      <p className="text-[11px] text-[#475569] mb-2">Pre-fill public details from the AI Blueprint and/or a website. Passwords, bank & logins are never auto-filled.</p>
                      <div className="flex flex-wrap gap-2">
                        <input value={autofillUrls[v.venture_id] || ''} onChange={(e) => setAutofillUrls((u) => ({ ...u, [v.venture_id]: e.target.value }))} placeholder="Website URL (optional)" data-testid={`venture-autofill-url-${v.venture_id}`} className="flex-1 min-w-[180px] px-3 py-2 border border-[#BFDBFE] rounded-lg text-sm bg-white focus:outline-none focus:border-[#1D4ED8]" />
                        <button type="button" onClick={() => autofill(v)} disabled={autofilling === v.venture_id} data-testid={`venture-autofill-btn-${v.venture_id}`} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-[#1D4ED8] text-white rounded-lg hover:bg-[#1E40AF] disabled:opacity-60">
                          {autofilling === v.venture_id ? <><Loader2 size={14} className="animate-spin" /> Filling…</> : <><Wand2 size={14} /> Auto-fill</>}
                        </button>
                      </div>
                    </div>
                    {groups.map((g) => (
                      <div key={g}>
                        <div className="text-[10px] font-black uppercase tracking-wider text-[#1D4ED8] mb-2">{g}</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {fields.filter((f) => f.group === g).map((f) => {
                            const val = (v.essentials || {})[f.id] || '';
                            const rk = `${v.venture_id}:${f.id}`;
                            const show = revealed[rk];
                            return (
                              <div key={f.id}>
                                <label className="flex items-center gap-1.5 text-xs font-semibold text-[#334155] mb-1">
                                  {val.trim() ? <CheckCircle2 size={13} className="text-emerald-500" /> : <Circle size={13} className="text-slate-300" />} {f.label}
                                  {f.sensitive && <Lock size={11} className="text-amber-500" title="Encrypted at rest" />}
                                </label>
                                <div className="relative">
                                  <input value={val} onChange={(e) => setEssential(v, f.id, e.target.value)} type={f.sensitive && !show ? 'password' : 'text'} autoComplete="off" data-testid={`venture-field-${v.venture_id}-${f.id}`} className={`w-full px-3 py-2 ${f.sensitive ? 'pr-9' : ''} border border-[#E2E8F0] rounded-lg text-sm bg-white focus:outline-none focus:border-[#1D4ED8]`} />
                                  {f.sensitive && <button type="button" onClick={() => setRevealed((r) => ({ ...r, [rk]: !r[rk] }))} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#334155]" aria-label="toggle">{show ? <EyeOff size={15} /> : <Eye size={15} />}</button>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    {allowDelete && (
                      <div className="pt-2 flex justify-between items-center">
                        <a href={`${API_URL}${base}/ventures/${v.venture_id}/pdf${q}`} target="_blank" rel="noopener noreferrer" data-testid={`venture-pdf-${v.venture_id}`} className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1D4ED8] hover:underline"><Download size={13} /> Download PDF</a>
                        <button type="button" onClick={() => removeVenture(v.venture_id)} data-testid={`venture-delete-${v.venture_id}`} className="inline-flex items-center gap-1.5 text-xs font-bold text-red-500 hover:text-red-600"><Trash2 size={13} /> Delete venture</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VenturesPanel;
